import { createSelector } from '@ngrx/store';

import { createActionsWithReducer } from '@core/ngrx-legacy';
import type { ReportingPermissions, ReportsAbilities } from '@core/reports-types';

import { safeReportsStateSelector } from '../helpers/safe-selector';

const namespace = 'ReportsPermissions';

type PermissionsState = {
  data: ReportingPermissions | null;
  pending: boolean;
  loaded: boolean;
  failed: boolean;
  error: unknown;
};

const permissionsInitialState: PermissionsState = {
  data: null,
  pending: false,
  loaded: false,
  failed: false,
  error: null,
};

const { actions, reducer } = createActionsWithReducer(namespace, permissionsInitialState, {
  fetch: (state): PermissionsState => ({ ...state, pending: true, failed: false, error: null }),
  fetchSuccess: (state, { data }: { data: ReportingPermissions }): PermissionsState => ({
    ...state,
    data,
    pending: false,
    loaded: true,
  }),
  fetchFailed: (state, { error }: { error: unknown }): PermissionsState => ({
    ...state,
    pending: false,
    failed: true,
    error,
  }),
});

const baseSelector = (root: any): PermissionsState =>
  safeReportsStateSelector(root, namespace, permissionsInitialState);

const dataSelector = createSelector(baseSelector, state => state?.data ?? null);

// Maps Analytics API permissions to the ReportsAbilities shape consumed by the reports feature.
// canManageReports covers both managing dashboards and creating custom ones (same privilege level).
const abilitiesSelector = createSelector(dataSelector, (permissions): ReportsAbilities | null => {
  if (!permissions) return null;
  return {
    canViewReportDashboards: permissions.canViewReports,
    canManageReportDashboards: permissions.canManageReports,
    canCreateCustomDashboards: permissions.canManageReports,
    canExportData: permissions.canExportReports,
  };
});

export {
  actions as ReportsPermissionsActions,
  namespace as ReportsPermissionsNamespace,
  reducer as ReportsPermissionsReducer,
};

export const ReportsPermissionsSelectors = {
  data: dataSelector,
  abilities: abilitiesSelector,
};
